import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DataGrid } from "@mui/x-data-grid";
import { getFieldData } from "./FOFOReport";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import FileSaver from "file-saver";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../EboPaymentWorking";

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	if (data && typeof data === "object") return [data];
	return [];
};

const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

function FOFOCommissionMaster() {
	const [commissionRows, setCommissionRows] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [displayFranchise, setDisplayFranchise] = useState("");
	const [franchise, setFranchiseList] = useState([]);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

    const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	
	const roleAccessData = roleAccess(user);

    const handleGetStoreFranchiseFetch = async () => {
            try {
                setLoadingOpen(true);
                await pythonVmInstance({
                    url: `/ebo-payment-working/master`,
                    headers: {
                        "Content-Type": "application/json",
                    },
                    params: {
                        master_type: "commission",
                    },
                    method: "GET",
                }).then((res) => {
                    setFranchiseList(getFieldData(res?.data));
                });
                setLoadingOpen(false);
            } catch (err) {
                setSnackBarOpen(true);
                setLoadingOpen(false);
                setSnackBar({
                    ...snackBar,
                    message: "Failed to fetch franchise file",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
            }
        };

    useEffect(() => {
        handleGetStoreFranchiseFetch();
    }, []);

	const handleFileSelect = useCallback((event) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";

		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			setError("Please select a CSV file.");
			setSuccess("");
			return;
		}

		setSelectedFile(file);
		setUploadedFileName(file.name);
		setError("");
		setSuccess("");
	}, []);

	const handleFileUpload = async (e) => {
			e.preventDefault();
			setLoadingOpen(true);
			let form = new FormData();
			if (selectedFile === "") {
				// setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Please upload files and try again",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
				return;
			}
	
			form.append("files", selectedFile);
			form.append("email", user.email);
			form.append("store_type", "fofo");

			pythonVmInstance
				.post("/ebo-payment-working/upload-comm", form, { headers: { "Content-Type": "multipart/form-data" } })
				.then((res) => {
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Uploaded the files successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
	
					setSelectedFile("")
					setUploadedFileName("")

				})
				.catch((err) => {
					const message = err?.response?.data?.message;
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: message ? message : "Unable to uploaded the files",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				});
		};

	const handleDownloadComm = async () => {
		try {
			setLoadingOpen(true);
			const params = new URLSearchParams({
				store_type: "fofo",
			});
			await pythonVmInstance({
				url: `/ebo-payment-working/franchise-comm?${params.toString()}`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
				responseType: "blob",
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `fofo_commission_master.csv`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: "Failed to download file",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const handleGetStores = async () => {
		if (!displayFranchise) return;

		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: `/ebo-payment-working/franchise-stores`,
				headers: {
					"Content-Type": "application/json",
				},
				params: {
					store_type: "fofo",
					franchise: displayFranchise,
				},
				method: "GET",
			});

			const rows = getResponseRows(res?.data).map((row, index) => ({
				id: row?.id ?? row?._id ?? `${displayFranchise}-${index + 1}`,
				...row,
			}));

			setCommissionRows(rows);
			setSuccess(rows.length ? "Stores fetched successfully." : "No stores found for selected franchise.");
		} catch (err) {
			setCommissionRows([]);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: err?.response?.data?.message || "Failed to fetch stores",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};


	const tableRows = useMemo(() => commissionRows, [commissionRows]);

	const columns = useMemo(() => {
		const firstRow = tableRows[0];
		if (!firstRow) return [];

		return Object.keys(firstRow)
			.filter((field) => field !== "id")
			.map((field) => ({
				field,
				headerName: formatHeaderName(field),
				flex: 1,
				minWidth: 150,
				valueFormatter: (value) => formatCellValue(value),
			}));
	}, [tableRows]);

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								Upload Commission File
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select franchise, choose CSV file, then upload.
							</Typography>
						</Box>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}> 
                                
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    size="small"
                                    sx={{ height: 38, px: 2 }}>
                                    Select CSV
                                    <input hidden accept=".csv,text/csv" type="file" onChange={handleFileSelect} />
                                </Button>


                                <Button
                                    variant="contained"
                                    onClick={handleFileUpload}
                                    disabled={!selectedFile || ["VIEWER", "EDITOR"].includes(roleAccessData.roles)} // Disable upload for view-only users
                                    size="small"
                                    sx={{ height: 38, px: 2 }}>
                                    Upload
                                </Button>
                            </Stack>
							<Button
								variant="outlined"
								startIcon={<FileDownloadIcon />}
								onClick={handleDownloadComm}
								size="small"
								sx={{ height: 38, px: 2 }}>
								Stores
							</Button>
						</Stack>

						{uploadedFileName ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
								Selected File: {uploadedFileName}
							</Typography>
						) : null}
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								Store Wise Commission
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select franchise and get stores to view commission in table.
							</Typography>
						</Box>

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
							<TextField
								select
								size="small"
								label="Select Franchise"
								value={displayFranchise}
								onChange={(event) => {
									setDisplayFranchise(event.target.value);
									setCommissionRows([]);
								}}
								sx={{
									minWidth: { xs: "100%", md: 240 },
									"& .MuiInputBase-root": {
										height: 38,
										borderRadius: 1.5,
									},
									"& .MuiInputBase-input": {
										py: 0.85,
										fontSize: 13,
									},
								}}>
								{franchise.length === 0 ? (
									<MenuItem value="" disabled>
										No franchise available
									</MenuItem>
								) : (
									franchise.map((franchise) => (
										<MenuItem key={franchise?.name} value={franchise?.name}>
											{franchise?.name}
										</MenuItem>
									))
								)}
							</TextField>

							<Button
								variant="contained"
								startIcon={<SearchIcon />}
								onClick={handleGetStores}
								disabled={!displayFranchise}
								size="small"
								sx={{ height: 38, px: 2 }}>
								Get Stores
							</Button>
						</Stack>

						{error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
						{success ? <Alert severity="success" sx={{ py: 0.5 }}>{success}</Alert> : null}
						<Box sx={{ height: { xs: 420, md: 460 }, width: "100%" }}>
							<DataGrid
								rows={tableRows}
								columns={columns}
								disableColumnMenu
								density="compact"
								pageSizeOptions={[25, 50, 100]}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 25, page: 0 },
									},
								}}
								sx={{
									border: "none",
									fontSize: 13,
									"& .MuiDataGrid-row": {
										minHeight: 36,
										maxHeight: 36,
									},
									"& .MuiDataGrid-cell": {
										py: 0.5,
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f5f5f5",
										minHeight: 40,
										maxHeight: 40,
									},
								}}
							/>
						</Box>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default FOFOCommissionMaster;
